import { Injectable } from '@angular/core';
import { persistState, PersistStateSelectFn, Store, StoreConfig } from '@datorama/akita';
import { AuthState, IdpModel, TwoFactorModel } from '../models';
import { SsoSessionModel } from '../models/sso-session.model';

const AUTH_STORE_NAME = 'auth';
const persistAuthStateProps: PersistStateSelectFn<AuthState> = ({ sso, token, twoFactor, redirectUrlAfterLogin }) => ({
  sso: {
    session: sso.session,
    idps: null,
  },
  token,
  twoFactor,
  redirectUrlAfterLogin,
});
persistAuthStateProps.storeName = AUTH_STORE_NAME;

export const authStoragePersistState = persistState({
  storage: localStorage,
  key: 'auth-storage',
  include: [AUTH_STORE_NAME],
  select: [persistAuthStateProps],
});

const initialState: AuthState = {
  sso: {
    session: null,
    idps: null,
  },
  token: null,
  tokenRefreshing: false,
  twoFactor: null,
  redirectUrlAfterLogin: null,
};

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: AUTH_STORE_NAME })
export class AuthStore extends Store<AuthState> {
  constructor() {
    super(initialState);
  }

  setToken(token: string) {
    this.update({ token, tokenRefreshing: false });
  }

  setTokenRefreshing() {
    this.update({ tokenRefreshing: true });
  }

  clearToken() {
    this.update({ token: null });
  }

  setRedirectUrlAfterLogin(redirectUrl: string | null) {
    this.update({ redirectUrlAfterLogin: redirectUrl });
  }

  setSsoSession(session: SsoSessionModel | null) {
    this.update((state) => ({
      sso: {
        ...state.sso,
        session,
      },
    }));
  }

  setSsoIdps(idps: IdpModel[]) {
    this.update((state) => {
      return {
        sso: {
          ...state.sso,
          idps: [...idps],
        },
      };
    });
  }

  addSsoIdp(idp: IdpModel) {
    this.update((state) => {
      return {
        sso: {
          ...state.sso,
          idps: [...(state.sso.idps || []), idp],
        },
      };
    });
  }

  updateSsoIdp(idp: IdpModel) {
    this.update((state) => {
      const idps = (state.sso?.idps || []).slice();
      const index = idps.findIndex((candidateIdp) => candidateIdp.public_id === idp.public_id);
      if (index === -1) {
        return;
      }
      idps[index] = idp;

      return {
        sso: {
          ...state.sso,
          idps,
        },
      };
    });
  }

  clearSsoIdps() {
    this.update((state) => {
      return {
        sso: {
          ...state.sso,
          idps: null,
        },
      };
    });
  }

  setTwoFactor(twoFactor: TwoFactorModel) {
    this.update((state) => ({
      ...state,
      twoFactor,
    }));
  }

  enableTwoFactor() {
    this.update((state) => ({
      ...state,
      twoFactor: {
        enabled: true,
      },
    }));
  }

  disableTwoFactor() {
    this.clearTwoFactorState();
  }

  clearTwoFactorState() {
    this.update((state) => ({
      ...state,
      twoFactor: null,
    }));
  }
}
